import type {
  TelegramBotIdentity, TelegramChatDiscovery, TelegramConfig, TelegramDetectedChat, TelegramRecipient,
} from '@erp/shared';
import { TELEGRAM_EVENT_TYPES } from '@erp/shared';
import {
  AlertTriangle, Bot, Check, ExternalLink, KeyRound, Plus, RefreshCw, Search, Send, Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Badge, Button, Card, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { formatTime } from '../../lib/intl.js';
import {
  addTelegramRecipient, deleteTelegramRecipient, detectTelegramChats, getTelegramBot, getTelegramConfig,
  createTelegramLinkCode, deleteTelegramLink, listTelegramLinks, type TelegramLink,
  listTelegramRecipients, sendTelegramTest, updateTelegramConfig, updateTelegramRecipient,
} from '../../lib/telegram.js';

// i18next splits keys on '.', but event ids are dotted — map each to a flat key.
const EVENT_LABEL_KEY: Record<string, string> = {
  'pos.sale.finalized': 'events.posSale',
  'store.order.counter': 'events.counterSale',
  'store.order.placed': 'events.orderPlaced',
  'store.order.app': 'events.orderApp',
  'store.order.website': 'events.orderWebsite',
  'store.order.mandob': 'events.orderMandob',
  'invoice.posted': 'events.invoicePosted',
  'payment.received': 'events.paymentReceived',
  'fib.order_paid': 'events.fibOrderPaid',
  'hr.payroll.posted': 'events.payrollPosted',
  'hr.attendance.punched': 'events.attendancePunched',
  'customer.type_requested': 'events.customerTypeRequested',
  'crm.lead.converted': 'events.leadConverted',
  'delivery.delivered': 'events.deliveryDelivered',
  'coupon.offline_unverified': 'events.couponUnverified',
  'fx.rate_changed': 'events.fxRateChanged',
};
/** One-liner under an event whose exact scope isn't obvious from the label. */
const EVENT_HINT_KEY: Record<string, string> = {
  'pos.sale.finalized': 'events.posSaleHint',
  'store.order.counter': 'events.counterSaleHint',
  'store.order.placed': 'events.orderPlacedHint',
  'coupon.offline_unverified': 'events.couponUnverifiedHint',
  'fib.order_paid': 'events.fibOrderPaidHint',
  'customer.type_requested': 'events.customerTypeRequestedHint',
  'hr.attendance.punched': 'events.attendancePunchedHint',
};
/**
 * The three "…and only from here" boxes. They narrow the order event above
 * rather than adding a new one, so they are indented under it — ticking one
 * without the umbrella is how a shop hears about the phone and nothing else.
 */
const ORDER_SOURCE_EVENTS = new Set(['store.order.app', 'store.order.website', 'store.order.mandob']);
type TFn = ReturnType<typeof useTranslation>['t'];
function eventLabel(t: TFn, ev: string): string {
  return t(EVENT_LABEL_KEY[ev] ?? 'events.other', { defaultValue: ev });
}

function EventChecklist({ value, onChange, allLabel }: {
  value: string[]; onChange: (next: string[]) => void; allLabel?: string;
}): JSX.Element {
  const { t } = useTranslation('telegram');
  const toggle = (ev: string): void => onChange(value.includes(ev) ? value.filter((x) => x !== ev) : [...value, ev]);
  return (
    <div className="flex flex-col gap-1.5">
      {allLabel && (
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" className="h-4 w-4 accent-brand" checked={value.length === 0} onChange={() => onChange([])} />
          {allLabel}
        </label>
      )}
      {TELEGRAM_EVENT_TYPES.map((ev) => {
        const hintKey = EVENT_HINT_KEY[ev];
        // ms-/ps- (not ml-/pl-) so the nesting flips with the page in RTL.
        const nested = ORDER_SOURCE_EVENTS.has(ev);
        return (
          <div key={ev} className={nested ? 'ms-6 border-s border-border ps-3' : undefined}>
            <label className="flex items-center gap-2 text-sm text-fg">
              <input type="checkbox" className="h-4 w-4 accent-brand" checked={value.includes(ev)} onChange={() => toggle(ev)} />
              {eventLabel(t, ev)}
            </label>
            {hintKey && <p className="ms-6 text-xs text-fg-muted">{t(hintKey)}</p>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The "who am I talking to" card. `getMe` gives us the bot behind the stored
 * token, so the page can name it (@duhokstationerybot) and link straight to it
 * instead of leaving staff to trust that a pasted secret was the right one.
 * Every failure mode is a state here, not an error toast.
 */
function BotIdentityCard({ identity, refreshing, onRefresh }: {
  identity: TelegramBotIdentity; refreshing: boolean; onRefresh: () => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('telegram');
  const bot = identity.bot;
  const connected = identity.status === 'connected' && bot !== null;

  const tone = connected ? 'border-success/30 bg-success/[0.06]'
    : identity.status === 'no_token' ? 'border-border'
    : 'border-danger/30 bg-danger/[0.06]';

  return (
    <Card className={`flex flex-col gap-4 border p-6 ${tone}`}>
      <div className="flex flex-wrap items-start gap-4">
        <span
          aria-hidden
          className={[
            'grid h-14 w-14 shrink-0 place-items-center rounded-2xl',
            connected ? 'bg-brand/15 text-brand' : 'bg-bg-muted text-fg-subtle',
          ].join(' ')}
        >
          <Bot className="h-7 w-7" />
        </span>

        <div className="min-w-0 flex-1">
          {connected ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-semibold text-fg">{bot.firstName}</h2>
                <Badge tone="success">{t('botCard.connected', { defaultValue: 'Connected' })}</Badge>
              </div>
              {bot.username && (
                <p className="mt-0.5 truncate font-mono text-sm text-fg-muted" dir="ltr">@{bot.username}</p>
              )}
              <p className="mt-1 text-xs text-fg-subtle">
                {t('botCard.checkedAt', {
                  defaultValue: 'Checked at {{time}}',
                  time: formatTime(identity.checkedAt, { locale: i18n.language }),
                })}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-fg">
                {identity.status === 'no_token'
                  ? t('botCard.none', { defaultValue: 'No bot connected yet' })
                  : identity.status === 'rejected'
                    ? t('botCard.rejected', { defaultValue: 'Telegram rejected this bot token' })
                    : t('botCard.unreachable', { defaultValue: 'Could not reach Telegram' })}
              </h2>
              <p className="mt-1 max-w-xl text-sm text-fg-muted">
                {identity.status === 'no_token'
                  ? t('botCard.noneHint', { defaultValue: 'Create a bot with @BotFather in Telegram, then paste its token below to connect it.' })
                  : identity.status === 'rejected'
                    ? t('botCard.rejectedHint', { defaultValue: 'The token was revoked or mistyped. Paste a fresh one from @BotFather below.' })
                    : t('botCard.unreachableHint', { defaultValue: 'Telegram did not answer. Check the server’s outbound connection, then try again.' })}
              </p>
              {identity.error && (
                <p className="mt-2 rounded-lg bg-bg-muted px-3 py-2 font-mono text-xs text-danger" dir="ltr">
                  {identity.error}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {connected && bot.link && (
            <a
              href={bot.link}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-bg px-3 text-sm font-medium text-fg outline-none transition hover:bg-bg-subtle focus-visible:ring-2 focus-visible:ring-brand/30"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              {t('botCard.open', { defaultValue: 'Open the bot' })}
            </a>
          )}
          <Button variant="ghost" size="sm" onClick={onRefresh} loading={refreshing}>
            <RefreshCw className="h-4 w-4" aria-hidden />
            {t('botCard.refresh', { defaultValue: 'Refresh' })}
          </Button>
        </div>
      </div>

      {connected && identity.webhookHost && (
        <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/[0.08] px-3 py-2 text-xs text-fg-muted">
          <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-warning" aria-hidden />
          {t('botCard.webhook', {
            defaultValue: 'A webhook is registered for this bot ({{host}}). Chat detection is unavailable while it is set.',
            host: identity.webhookHost,
          })}
        </p>
      )}
      {connected && bot.canReadAllGroupMessages === false && (
        <p className="text-xs text-fg-subtle">
          {t('botCard.privacy', { defaultValue: 'Privacy mode is on: inside groups the bot only sees commands and replies. That is enough to detect the group.' })}
        </p>
      )}
    </Card>
  );
}

/** Compact "is this actually going to fire?" strip under the bot card. */
function StatusStrip({ config, recipients }: { config: TelegramConfig; recipients: TelegramRecipient[] }): JSX.Element {
  const { t } = useTranslation('telegram');
  const active = recipients.filter((r) => r.isActive).length;
  const items: { label: string; ok: boolean }[] = [
    {
      label: config.enabled
        ? t('strip.on', { defaultValue: 'Notifications on' })
        : t('strip.off', { defaultValue: 'Notifications off' }),
      ok: config.enabled,
    },
    {
      label: t('strip.events', { defaultValue: '{{n}} events selected', n: config.events.length }),
      ok: config.events.length > 0,
    },
    {
      label: t('strip.recipients', { defaultValue: '{{n}} active recipients', n: active }),
      ok: active > 0,
    },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <span
          key={it.label}
          className={[
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium',
            it.ok ? 'border-success/30 bg-success/10 text-success' : 'border-border bg-bg-subtle text-fg-muted',
          ].join(' ')}
        >
          {it.ok ? <Check className="h-3.5 w-3.5" aria-hidden /> : <AlertTriangle className="h-3.5 w-3.5" aria-hidden />}
          {it.label}
        </span>
      ))}
    </div>
  );
}

export function TelegramSettingsPage(): JSX.Element {
  const { t } = useTranslation('telegram');
  const { t: tc } = useTranslation();
  const [config, setConfig] = useState<TelegramConfig | null>(null);
  const [recipients, setRecipients] = useState<TelegramRecipient[] | null>(null);
  const [identity, setIdentity] = useState<TelegramBotIdentity | null>(null);
  const [refreshingBot, setRefreshingBot] = useState(false);

  // config form
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);
  /** Result of the last test send, shown inline so the reason survives a toast. */
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  // Two-way assistant
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  const [links, setLinks] = useState<TelegramLink[] | null>(null);
  const [linkCode, setLinkCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [linking, setLinking] = useState(false);


  /** Mint a code for MY account, to send the bot as /link CODE. */
  const makeLinkCode = async (): Promise<void> => {
    setLinking(true);
    try { setLinkCode(await createTelegramLinkCode()); }
    catch { toast.error(tc('errors:server')); }
    finally { setLinking(false); }
  };

  const revokeLink = async (id: string): Promise<void> => {
    try {
      await deleteTelegramLink(id);
      setLinks(await listTelegramLinks());
      toast.success(t('assistant.revoked', { defaultValue: 'Link removed' }));
    } catch { toast.error(tc('errors:server')); }
  };

  const [showAdd, setShowAdd] = useState(false);
  const [showDetect, setShowDetect] = useState(false);
  const [editing, setEditing] = useState<TelegramRecipient | null>(null);

  const loadAll = async (): Promise<void> => {
    try {
      const [c, r, b] = await Promise.all([getTelegramConfig(), listTelegramRecipients(), getTelegramBot()]);
      setConfig(c); setEnabled(c.enabled); setEvents(c.events); setRecipients(r); setIdentity(b);
      setAssistantEnabled(c.assistantEnabled);
      // Links are admin-only; a non-admin still gets the rest of the page.
      try { setLinks(await listTelegramLinks()); } catch { setLinks([]); }
    } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => { void loadAll(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const reloadRecipients = async (): Promise<void> => {
    try { setRecipients(await listTelegramRecipients()); } catch { toast.error(tc('errors:server')); }
  };

  const refreshBot = async (): Promise<void> => {
    setRefreshingBot(true);
    try { setIdentity(await getTelegramBot(true)); }
    catch { toast.error(tc('errors:server')); }
    finally { setRefreshingBot(false); }
  };

  const saveConfig = async (): Promise<void> => {
    setSavingCfg(true);
    const tokenChanged = Boolean(token.trim());
    try {
      const c = await updateTelegramConfig({
        enabled, events, assistantEnabled,
        ...(tokenChanged ? { botToken: token.trim() } : {}),
      });
      setConfig(c); setEnabled(c.enabled); setEvents(c.events); setToken(''); setAssistantEnabled(c.assistantEnabled);
      toast.success(tc('actions.saved', { defaultValue: 'Saved' }));
      // A new token means a (possibly) different bot — re-ask Telegram who it is.
      if (tokenChanged) await refreshBot();
    } catch { toast.error(tc('errors:server')); } finally { setSavingCfg(false); }
  };

  const clearToken = async (): Promise<void> => {
    setSavingCfg(true);
    try {
      const c = await updateTelegramConfig({ botToken: null });
      setConfig(c); setToken(''); setTestResult(null);
      await refreshBot();
      toast.success(t('tokenCleared', { defaultValue: 'Bot token removed' }));
    } catch { toast.error(tc('errors:server')); } finally { setSavingCfg(false); }
  };

  const runTest = async (chatId?: string): Promise<void> => {
    setTesting(true); setTestResult(null);
    try {
      const res = await sendTelegramTest(chatId);
      if (res.failed === 0) {
        const text = t('testOk', { defaultValue: 'Sent to {{n}} chat(s)', n: res.sent });
        setTestResult({ ok: true, text }); toast.success(text);
      } else {
        const text = t('testFail', { defaultValue: 'Sent {{s}}, failed {{f}}: {{e}}', s: res.sent, f: res.failed, e: res.error ?? '' });
        setTestResult({ ok: false, text }); toast.error(text);
      }
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      const text = code === 'no_bot_token' ? t('needToken', { defaultValue: 'Set a bot token first.' })
        : code === 'no_recipients' ? t('needRecipients', { defaultValue: 'Add a recipient first.' })
        : tc('errors:server');
      setTestResult({ ok: false, text }); toast.error(text);
    } finally { setTesting(false); }
  };

  if (!config || !recipients || !identity) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-5 h-56 w-full" />
      </Card>
    );
  }

  const columns = [
    {
      id: 'label', header: t('recipient.label', { defaultValue: 'Name' }),
      cell: (r: TelegramRecipient) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{r.label}</div>
          <div className="truncate font-mono text-xs text-fg-muted" dir="ltr">{r.chatId}</div>
        </div>
      ),
      searchValue: (r: TelegramRecipient) => `${r.label} ${r.chatId}`,
    },
    {
      id: 'events', header: t('recipient.events', { defaultValue: 'Events' }),
      cell: (r: TelegramRecipient) => (
        <span className="text-xs text-fg-muted">
          {!r.events || r.events.length === 0
            ? t('recipient.allEvents', { defaultValue: 'All enabled' })
            : r.events.map((e) => eventLabel(t, e)).join(', ')}
        </span>
      ),
    },
    {
      id: 'status', header: tc('columns.status', { defaultValue: 'Status' }),
      cell: (r: TelegramRecipient) => <Badge tone={r.isActive ? 'success' : 'neutral'}>{tc(`status.${r.isActive ? 'active' : 'inactive'}`, { defaultValue: r.isActive ? 'Active' : 'Inactive' })}</Badge>,
    },
    {
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: TelegramRecipient) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => void runTest(r.chatId)} disabled={testing}>{t('test', { defaultValue: 'Test' })}</Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>{tc('actions.edit')}</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('title', { defaultValue: 'Telegram notifications' })}</h1>
        <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Push new sales, invoices and payments to a Telegram bot.' })}</p>
      </header>

      <BotIdentityCard identity={identity} refreshing={refreshingBot} onRefresh={() => void refreshBot()} />
      <StatusStrip config={config} recipients={recipients} />

      {/* Bot configuration */}
      <Card className="flex flex-col gap-5 p-6">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <KeyRound className="h-5 w-5 text-fg-muted" aria-hidden />
            {t('bot.title', { defaultValue: 'Bot' })}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">
            {t('bot.description', { defaultValue: 'The token identifies the bot that sends your notifications. It is stored encrypted and never shown again.' })}
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" className="h-4 w-4 accent-brand" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {t('bot.enabled', { defaultValue: 'Send notifications to Telegram' })}
        </label>

        <FormField
          id="tg-token"
          label={t('bot.token', { defaultValue: 'Bot token' })}
          helperText={t('bot.tokenHint', { defaultValue: 'Create a bot with @BotFather in Telegram, then paste its token here.' })}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="tg-token"
              type="password"
              autoComplete="new-password"
              name="telegram-bot-token"
              className="flex-1 font-mono"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder={config.botTokenSet ? t('bot.tokenSet', { defaultValue: '•••••••• (set — type to replace)' }) : '123456789:AA...'}
            />
            {config.botTokenSet && (
              <Button type="button" variant="ghost" size="sm" onClick={clearToken} disabled={savingCfg}>{t('bot.clearToken', { defaultValue: 'Remove' })}</Button>
            )}
          </div>
        </FormField>

        <div>
          <div className="mb-1.5 text-sm font-medium text-fg">{t('bot.events', { defaultValue: 'Notify on' })}</div>
          <p className="mb-2 text-xs text-fg-muted">{t('bot.eventsHint', { defaultValue: 'Only the events ticked here are forwarded, and only to recipients that accept them.' })}</p>
          <EventChecklist value={events} onChange={setEvents} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={saveConfig} loading={savingCfg}>{tc('actions.save')}</Button>
          <Button variant="secondary" onClick={() => void runTest()} loading={testing} disabled={!config.botTokenSet}>
            <Send className="h-4 w-4" aria-hidden />{t('sendTest', { defaultValue: 'Send test' })}
          </Button>
        </div>

        {testResult && (
          <p
            role={testResult.ok ? 'status' : 'alert'}
            className={[
              'rounded-lg border px-3 py-2 text-sm',
              testResult.ok ? 'border-success/30 bg-success/[0.08] text-success' : 'border-danger/30 bg-danger/[0.08] text-danger',
            ].join(' ')}
          >
            {testResult.text}
          </p>
        )}
      </Card>

      {/* Two-way assistant — talk to the ERP from Telegram */}
      <Card className="flex flex-col gap-5 p-6">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('assistant.title', { defaultValue: 'Chat with the assistant' })}
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            {t('assistant.subtitle', { defaultValue: 'Message the bot and get the same assistant as the dashboard. It answers with YOUR permissions, and only in a private chat — never in a group.' })}
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand"
            checked={assistantEnabled}
            onChange={(e) => setAssistantEnabled(e.target.checked)}
            disabled={!config?.botTokenSet}
          />
          {t('assistant.enable', { defaultValue: 'Let staff chat with the assistant' })}
        </label>
        {!config?.botTokenSet && (
          <p className="text-xs text-fg-subtle">{t('needToken', { defaultValue: 'Connect a bot token first.' })}</p>
        )}
        {assistantEnabled && (
          <p className="text-xs text-warning">
            {t('assistant.pollingNote', { defaultValue: 'While this is on, Telegram delivers messages to this server, so the “Find chats” button below can no longer list chats — add a recipient by its chat id instead.' })}
          </p>
        )}

        {/* Link MY Telegram */}
        <div className="rounded-lg border border-dashed border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{t('assistant.linkMine', { defaultValue: 'Connect my Telegram' })}</div>
              <div className="text-xs text-fg-muted">
                {t('assistant.linkHint', { defaultValue: 'Generate a code, then send it to the bot as: /link CODE' })}
              </div>
            </div>
            <Button size="sm" variant="secondary" loading={linking} onClick={() => void makeLinkCode()}>
              {t('assistant.getCode', { defaultValue: 'Get a code' })}
            </Button>
          </div>
          {linkCode && (
            <div className="mt-3 rounded-md border border-brand/30 bg-brand/[0.06] px-3 py-2">
              <div className="font-mono text-lg font-bold tracking-widest text-brand">/link {linkCode.code}</div>
              <div className="mt-1 text-xs text-fg-muted">
                {t('assistant.codeExpires', { defaultValue: 'Send this to the bot within 10 minutes. It works once.' })}
              </div>
            </div>
          )}
        </div>

        {/* Who is connected */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {t('assistant.connected', { defaultValue: 'Connected accounts' })}
          </div>
          {links === null ? (
            <Skeleton className="h-10 w-full" />
          ) : links.length === 0 ? (
            <p className="text-sm text-fg-muted">{t('assistant.none', { defaultValue: 'Nobody has connected a Telegram account yet.' })}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {links.map((l) => (
                <li key={l.id} className="flex items-center gap-3 rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{l.userName ?? l.userId}</span>
                    <span className="block truncate text-xs text-fg-muted">{l.telegramLabel ?? '—'}</span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => void revokeLink(l.id)}>
                    {t('assistant.revoke', { defaultValue: 'Disconnect' })}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      {/* Recipients */}
      <Card className="flex flex-col gap-3 p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Users className="h-5 w-5 text-fg-muted" aria-hidden />
              {t('recipients.title', { defaultValue: 'Recipients' })}
            </h2>
            <p className="mt-0.5 text-xs text-fg-muted">{t('recipients.hint', { defaultValue: 'Each chat, group, or channel that should receive notifications.' })}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setShowDetect(true)} disabled={!config.botTokenSet}>
              <Search className="h-4 w-4" aria-hidden />{t('detect.open', { defaultValue: 'Find my chat' })}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" aria-hidden />{t('recipients.add', { defaultValue: 'Add recipient' })}
            </Button>
          </div>
        </div>
        {recipients.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title={t('recipients.empty', { defaultValue: 'No recipients yet' })}
              description={t('recipients.emptyHint', { defaultValue: 'Send /start to your bot in Telegram, then use “Find my chat” — no chat ID hunting needed.' })}
              action={(
                <Button size="sm" onClick={() => setShowDetect(true)} disabled={!config.botTokenSet}>
                  <Search className="h-4 w-4" aria-hidden />{t('detect.open', { defaultValue: 'Find my chat' })}
                </Button>
              )}
            />
          </div>
        ) : (
          <DataTable columns={columns} data={recipients} rowKey={(r) => r.id} searchPlaceholder={t('recipients.search', { defaultValue: 'Search recipients…' })} />
        )}
      </Card>

      {showDetect && <DetectChatsDialog onClose={() => setShowDetect(false)} onDone={reloadRecipients} />}
      {showAdd && <RecipientDialog onClose={() => setShowAdd(false)} onDone={reloadRecipients} />}
      {editing && <RecipientDialog recipient={editing} onClose={() => setEditing(null)} onDone={reloadRecipients} />}
    </div>
  );
}

/**
 * "Find my chat" — asks Telegram which chats the bot can currently see and
 * turns each into a one-click recipient. Replaces the old flow of telling
 * people to go and look their numeric chat id up in another bot.
 */
function DetectChatsDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation('telegram');
  const { t: tc } = useTranslation();
  const [data, setData] = useState<TelegramChatDiscovery | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  /** Chats added during this dialog — flipped to "Added" without a re-poll. */
  const [justAdded, setJustAdded] = useState<Record<string, true>>({});

  const load = async (): Promise<void> => {
    setLoading(true); setFailed(false);
    try { setData(await detectTelegramChats()); }
    catch { setFailed(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const add = async (chat: TelegramDetectedChat): Promise<void> => {
    setAddingId(chat.id);
    try {
      await addTelegramRecipient({ label: chat.title, chatId: chat.id, isActive: true, events: null });
      setJustAdded((m) => ({ ...m, [chat.id]: true }));
      await onDone();
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      if (code === 'chat_id_taken') setJustAdded((m) => ({ ...m, [chat.id]: true }));
      else toast.error(tc('errors:server'));
    } finally { setAddingId(null); }
  };

  const typeLabel = (type: TelegramDetectedChat['type']): string => (
    type === 'private' ? t('detect.types.private', { defaultValue: 'Private chat' })
    : type === 'channel' ? t('detect.types.channel', { defaultValue: 'Channel' })
    : t('detect.types.group', { defaultValue: 'Group' })
  );

  const notice = (): JSX.Element | null => {
    if (failed) return <Notice tone="danger">{tc('errors:server')}</Notice>;
    if (!data) return null;
    switch (data.status) {
      case 'no_token':
        return <Notice tone="warning">{t('detect.noToken', { defaultValue: 'Connect a bot token first.' })}</Notice>;
      case 'webhook_set':
        return (
          <Notice tone="warning">
            {data.webhookHost
              ? t('detect.webhookSet', {
                defaultValue: 'A webhook is registered for this bot ({{host}}). Telegram does not allow chat detection while a webhook is set — remove it, or add the chat ID by hand below.',
                host: data.webhookHost,
              })
              : t('detect.webhookSetPlain', {
                defaultValue: 'A webhook is registered for this bot. Telegram does not allow chat detection while a webhook is set — remove it, or add the chat ID by hand.',
              })}
          </Notice>
        );
      case 'rejected':
        return <Notice tone="danger">{t('detect.rejected', { defaultValue: 'Telegram rejected the bot token: {{error}}', error: data.error ?? '' })}</Notice>;
      case 'unreachable':
        return <Notice tone="danger">{t('detect.unreachable', { defaultValue: 'Could not reach Telegram: {{error}}', error: data.error ?? '' })}</Notice>;
      default:
        return null;
    }
  };

  const chats = data?.status === 'ok' ? data.chats : [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('detect.title', { defaultValue: 'Find my chat' })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <p className="rounded-lg border border-border bg-bg-subtle px-3 py-2 text-xs text-fg-muted">
            {t('detect.hint', { defaultValue: 'To appear here: open the bot in Telegram and send /start, or add the bot to a group and post one message. Then press Refresh.' })}
          </p>

          {notice()}

          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : chats.length === 0 ? (
            // Only the "we asked and Telegram had nothing" case gets the empty
            // state — every other outcome is already explained by notice().
            data?.status === 'ok' ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
                <p className="text-sm font-medium text-fg">{t('detect.empty', { defaultValue: 'No chats found yet' })}</p>
                <p className="mt-1 text-xs text-fg-muted">{t('detect.emptyHint', { defaultValue: 'Telegram only reports recent activity, so message the bot first and then refresh.' })}</p>
              </div>
            ) : null
          ) : (
            <ul className="flex max-h-80 flex-col divide-y divide-border overflow-y-auto rounded-lg border border-border">
              {chats.map((c) => {
                const added = c.alreadyAdded || justAdded[c.id] === true;
                return (
                  <li key={c.id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-fg">{c.title}</div>
                      <div className="truncate text-xs text-fg-muted">
                        {typeLabel(c.type)}
                        <span className="mx-1.5 text-fg-subtle">·</span>
                        <span className="font-mono" dir="ltr">{c.id}</span>
                        {c.username && <span className="ms-1.5 font-mono" dir="ltr">@{c.username}</span>}
                      </div>
                    </div>
                    {added ? (
                      <Badge tone="success" className="shrink-0 gap-1">
                        <Check className="h-3.5 w-3.5" aria-hidden />
                        {t('detect.added', { defaultValue: 'Added' })}
                      </Badge>
                    ) : (
                      <Button size="sm" className="shrink-0" onClick={() => void add(c)} loading={addingId === c.id}>
                        <Plus className="h-4 w-4" aria-hidden />{t('detect.add', { defaultValue: 'Add' })}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {data?.status === 'ok' && (
            <p className="text-xs text-fg-subtle">
              {t('detect.note', { defaultValue: 'Telegram only keeps recent, unread updates — a chat you last used days ago will not show up here.' })}
            </p>
          )}
        </div>

        <DialogFooter className="justify-between">
          <Button type="button" variant="ghost" onClick={() => void load()} loading={loading}>
            <RefreshCw className="h-4 w-4" aria-hidden />{t('detect.refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>{t('detect.close', { defaultValue: 'Close' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Notice({ tone, children }: { tone: 'warning' | 'danger'; children: React.ReactNode }): JSX.Element {
  return (
    <p
      role="alert"
      className={[
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-xs',
        tone === 'warning' ? 'border-warning/30 bg-warning/[0.08] text-fg-muted' : 'border-danger/30 bg-danger/[0.08] text-danger',
      ].join(' ')}
    >
      <AlertTriangle className={`mt-px h-4 w-4 shrink-0 ${tone === 'warning' ? 'text-warning' : 'text-danger'}`} aria-hidden />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

function RecipientDialog({ recipient, onClose, onDone }: {
  recipient?: TelegramRecipient; onClose: () => void; onDone: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('telegram');
  const { t: tc } = useTranslation();
  const isEdit = Boolean(recipient);
  const [label, setLabel] = useState(recipient?.label ?? '');
  const [chatId, setChatId] = useState(recipient?.chatId ?? '');
  const [isActive, setIsActive] = useState(recipient?.isActive ?? true);
  const [events, setEvents] = useState<string[]>(recipient?.events ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload = { label: label.trim(), chatId: chatId.trim(), isActive, events: events.length > 0 ? events : null };
      if (recipient) await updateTelegramRecipient(recipient.id, payload);
      else await addTelegramRecipient(payload);
      onClose(); await onDone();
    } catch (err) {
      const code = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      setError(code === 'chat_id_taken' ? t('recipient.chatTaken', { defaultValue: 'That chat is already added.' }) : tc('errors:server'));
    } finally { setBusy(false); }
  };

  const remove = async (): Promise<void> => {
    if (!recipient) return;
    setBusy(true);
    try { await deleteTelegramRecipient(recipient.id); onClose(); await onDone(); }
    catch { setError(tc('errors:server')); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? recipient!.label : t('recipients.add', { defaultValue: 'Add recipient' })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-3">
            <FormField id="rc-label" label={t('recipient.label', { defaultValue: 'Name' })} required>
              <Input id="rc-label" value={label} onChange={(e) => setLabel(e.target.value)} required autoFocus placeholder={t('recipient.labelPlaceholder', { defaultValue: 'e.g. Sales channel' })} />
            </FormField>
            <FormField id="rc-chat" label={t('recipient.chatId', { defaultValue: 'Chat ID' })} required helperText={t('recipient.chatHint', { defaultValue: 'A user (123456789), group/channel (-100…), or @channelusername. Use “Find my chat” to fill this in automatically.' })}>
              <Input id="rc-chat" className="font-mono" dir="ltr" value={chatId} onChange={(e) => setChatId(e.target.value)} required placeholder="123456789" />
            </FormField>
            <div>
              <div className="mb-1.5 text-sm font-medium text-fg">{t('recipient.events', { defaultValue: 'Events' })}</div>
              <EventChecklist value={events} onChange={setEvents} allLabel={t('recipient.allEvents', { defaultValue: 'All enabled events' })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-fg">
              <input type="checkbox" className="h-4 w-4 accent-brand" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              {tc('status.active', { defaultValue: 'Active' })}
            </label>
          </div>
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
          <DialogFooter className="justify-between">
            {isEdit ? (
              <Button type="button" variant="ghost" className="text-danger hover:text-danger" onClick={remove} disabled={busy}>{tc('actions.delete', { defaultValue: 'Delete' })}</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
              <Button type="submit" loading={busy} disabled={!label.trim() || !chatId.trim()}>{tc('actions.save')}</Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
